import React, { useRef, ElementType } from 'react';

type PlayOnHoverProps<T extends ElementType> = {
  as?: T;
  videoSrc: string;
  posterSrc?: string;
  loop?: boolean;
  className?: string;
  children?: React.ReactNode;
  play?: boolean; // New prop to control playback externally
  onLoadedData?: () => void; // Callback when video metadata is loaded
} & Omit<React.ComponentPropsWithoutRef<T>, 'as'>;


const PlayOnHover = <T extends ElementType = 'div'>({
  as,
  videoSrc,
  posterSrc,
  loop = false,
  className,
  children,
  play = false,
  onLoadedData,
  ...rest
}: PlayOnHoverProps<T>) => {
  const Component = as || 'div';
  const videoRef = useRef<HTMLVideoElement>(null);
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = typeof window !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent);

  React.useEffect(() => {
    if (!videoRef.current) return;
    if (prefersReducedMotion) return;
    if (isMobile) return;
    if (play) {
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [play, loop, prefersReducedMotion, isMobile]);

  const [isPlaying, setIsPlaying] = React.useState(false);
  const handleClick = () => {
    if (!isMobile) return;
    if (!isPlaying) {
      setIsPlaying(true);
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      }
    } else {
      setIsPlaying(false);
      if (videoRef.current) {
        videoRef.current.pause();
        if (!loop) videoRef.current.currentTime = 0;
      }
    }
  };

  const showImage = isMobile ? !isPlaying : !play;

  return (
    <Component
      onClick={handleClick}
      className={className}
      {...rest}
    >
      {posterSrc && showImage && (
        <img
          src={posterSrc}
          alt="Video preview"
          loading="lazy"
          decoding="async"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 2,
            transition: 'opacity 0.2s',
            opacity: 1,
            background: '#eee',
          }}
        />
      )}
      <video
        ref={videoRef}
        src={videoSrc}
        poster={posterSrc}
        loop={loop}
        muted
        playsInline
        onLoadedData={onLoadedData}
        onEnded={() => {
          if (videoRef.current && !loop) {
            videoRef.current.pause();
            if (isMobile) setIsPlaying(false);
          }
        }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1,
          display: 'block',
          background: '#eee',
        }}
      >
      </video>
      {children}
    </Component>
  );
};

export default PlayOnHover;
